<template>
  <div class="fu-drc" :data-queue="detailPageName" data-test="fu-drawer-container">
    <div v-if="loadingData" class="fu-drc__state" role="status" data-test="fu-drawer-loading">
      <span class="fu-drc__spinner" aria-hidden="true"></span>
      <span>Loading the patient…</span>
    </div>

    <div v-else-if="!patient" class="fu-drc__state fu-drc__state--error" data-test="fu-drawer-unavailable">
      <h2 class="fu-drc__state-title">Unable to Load Patient Data</h2>
      <p>Either this patient has scheduled an appointment, or the claim has been received in Open Dental.</p>
      <p>Close this window and refresh the page to resolve the issue.</p>
      <button type="button" class="fu-drc__btn" data-test="fu-drawer-unavailable-close" @click="closeWithoutSaving">Close</button>
    </div>

    <drawer-layout v-else @layout="layout = $event">
      <template #header>
        <drawer-header
          :title="headerTitle"
          :name="patient.PatientName || ''"
          :demographics="demographics"
          :premed="premed"
          :contacts="contacts"
          :address="address"
          @open-profile="openProfile"
          @close="closeWithoutSaving"
        />
      </template>

      <template #facts="{ layout: l }">
        <drawer-facts :facts="facts" :layout="l" />
      </template>

      <template #progress>
        <drawer-attempt-progress :attempts="conditions.totalAttempts" :max="conditions.maxFollowups" />
      </template>

      <template #log>
        <PopupErrorDisplay />

        <drawer-alert
          :alert="alert"
          :total-attempts="conditions.totalAttempts"
          :max-followups="conditions.maxFollowups"
          :next-attempt-date="nextAttemptLabel"
          :assignee-name="assigneeName"
          :can-restart="canRestart"
          @restart="restartConfirm = true"
        />

        <div v-if="restartConfirm" class="fu-drc__confirm" role="alert" data-test="fu-drawer-restart-confirm">
          <p class="fu-drc__confirm-text">Restart the follow-up cycle for {{ firstName || 'this patient' }}? The attempt count goes back to 0.</p>
          <div class="fu-drc__confirm-actions">
            <button type="button" class="fu-drc__btn" :disabled="restarting" data-test="fu-drawer-restart-yes" @click="confirmRestart">Restart follow-ups</button>
            <button type="button" class="fu-drc__btn fu-drc__btn--ghost" :disabled="restarting" data-test="fu-drawer-restart-no" @click="restartConfirm = false">Keep as is</button>
          </div>
        </div>

        <drawer-early-wizard
          v-if="alert && alert.key === 'earlyWizard'"
          :step="alert.step"
          :scheduled-label="scheduledLabel"
          :moved-label="formatYmd(wizardMovedTo)"
          :today="today"
          :busy="wizardBusy"
          @move-today="onWizardMoveToday"
          @pick-date="onWizardPickDate"
        />

        <drawer-method-picker :methods="methodList" :active="method" @select="onSelectMethod">
          <template #upsell="{ method: upsellFor }">
            <FeatureRequestButton
              v-if="upsellFor === 'sms'"
              class="btn btn-sm btn-primary"
              feature-key="SMS"
              feature-label="2-Way SMS texting"
              source-page="Follow-up Drawer - Text"
            />
            <FeatureRequestButton
              v-else-if="upsellFor === 'email'"
              class="btn btn-sm btn-primary"
              feature-key="EMAIL"
              feature-label="Patient email"
              source-page="Follow-up Drawer - Email"
            />
            <FeatureRequestNote v-if="upsellFor === 'sms' || upsellFor === 'email'" :feature="upsellFor === 'sms' ? 'SMS' : 'EMAIL'" />
          </template>
        </drawer-method-picker>

        <drawer-phone-form
          v-if="method === 'phone'"
          :statuses="statusOptions"
          :status-id="statusID"
          :note="followupNote"
          :copies-to-commlog="copiesToCommlog"
          @status-change="onStatusChange"
          @note-input="followupNote = $event"
        >
          <drawer-next-date v-bind="nextDateProps" @override="onOverride" @use-office="onUseOffice" @change="onDateChange" />
          <drawer-outcome-note :kind="outcomeKind" :first-name="firstName" />
        </drawer-phone-form>

        <drawer-text-form
          v-else-if="method === 'sms'"
          :from-number="practiceNumber"
          :templates="smsTemplateOptions"
          :template-id="selectedWikiPageID"
          :message="followupNote"
          :preview-note="smsPreviewNote"
          @template-change="onSmsTemplateChange"
          @message-input="followupNote = $event"
        >
          <template #guidance>
            <SmsGuidance :text="followupNote || ''" substitute-tags @change="onSmsGuidance" />
          </template>
          <drawer-next-date v-bind="nextDateProps" @override="onOverride" @use-office="onUseOffice" @change="onDateChange" />
        </drawer-text-form>

        <drawer-email-form
          v-else-if="method === 'email'"
          :templates="emailTemplateOptions"
          :template-id="selectedWikiPageID"
          :to="patient.Email || ''"
          :note="followupNote"
          @template-change="onEmailTemplateChange"
        >
          <drawer-next-date v-bind="nextDateProps" @override="onOverride" @use-office="onUseOffice" @change="onDateChange" />
        </drawer-email-form>

        <drawer-payment-form
          v-else-if="method === 'payment'"
          :from-number="practiceNumber"
          :templates="paymentTemplateOptions"
          :template-id="selectedWikiPageID"
          :message="followupNote"
          @template-change="onSmsTemplateChange"
          @message-input="followupNote = $event"
        >
          <template #guidance>
            <SmsGuidance :text="followupNote || ''" substitute-tags @change="onSmsGuidance" />
          </template>
        </drawer-payment-form>

        <div v-else-if="method === 'ai'" class="fu-drc__ai">
          <drawer-ai-call-form :first-name="firstName" />
          <div v-if="aiCallResult" class="fu-drc__ai-result" data-test="fu-drawer-ai-result">
            <div class="fu-drc__ai-head">
              AI call result: <strong>{{ aiCallResult.outcome || 'unknown' }}</strong>
              <span class="fu-drc__ai-meta">{{ aiCallResult.mode || 'simulation' }}<template v-if="aiCallResult.duration"> · {{ aiCallResult.duration }}s</template></span>
            </div>
            <ul v-if="aiCallResult.transcript && aiCallResult.transcript.length" class="fu-drc__ai-transcript">
              <li v-for="(msg, idx) in aiCallResult.transcript" :key="idx">
                <strong>{{ msg.role === 'agent' ? 'AI Agent' : 'Patient' }}:</strong> {{ msg.text || msg.content || msg.message }}
              </li>
            </ul>
            <p v-else-if="aiCallResult.reason" class="fu-drc__ai-reason">{{ aiCallResult.reason }}</p>
          </div>
        </div>
      </template>

      <template #tabs>
        <drawer-tabs
          :tabs="tabs"
          :active="activeTab"
          :ctx="tabCtx"
          @select="activeTab = $event"
          @edit-note="onEditNote"
          @open-patient="openPatient"
        />
      </template>

      <template #footer>
        <drawer-footer
          :form-on="method !== null"
          :method="method || 'phone'"
          :save-label="saveState.label"
          :save-disabled="saveState.disabled"
          :saving="saving"
          :show-classic="showClassic"
          :lock-text="lockText"
          @use-classic="useClassicDrawer"
          @open-profile="openProfile"
          @cancel="closeWithoutSaving"
          @save="onSave"
        />
      </template>
    </drawer-layout>

    <StripePaymentModal
      v-if="isStripeConnect"
      ref="stripePaymentModal"
      :siteID="siteID"
      :patNum="String(patNum)"
      :patientName="patient ? (patient.PatientName || (patient.FName + ' ' + patient.LName)) : ''"
      :patientBalance="(drawerData && drawerData.patientBalance) || 0"
      :patientPhone="patient ? (patient.WirelessPhone || '') : ''"
      @payment-success="onStripeInOfficeSuccess"
    />
  </div>
</template>

<script>
/**
 * FollowupDrawer — the NEW follow-up drawer's container (drawer redesign Phase 6c, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-drawer-plan.md
 *
 * The ONE stateful piece of the new drawer. Same contract as today's CreateFollowup.vue (FROZEN),
 * so callers do not change: props teamioPatientID / claimNum / results / detailPageName / query,
 * and it closes with $emit('closePanel', result) — drawerPayload.buildCloseResult after a save,
 * closeWithoutSave otherwise. Two optional callbacks come from followupActions.openDrawer only:
 * onClassicDrawer (the Super Admin "Classic drawer" link) and onRestart (the alert's "Restart
 * follow-ups"); without them those controls are hidden.
 *
 *   data       teamio/runQuery(query) unless `results` was passed (as today), then
 *              drawerQueues.readDrawerData — a COPY of the result sets, so the wizard's local date
 *              change never writes into the caller's object.
 *   decisions  the rules in src/utils/followups/drawer/ — alerts, methods, next date, payloads, tabs,
 *              facts. Nothing here re-derives a rule; the shared helpers' RESULTS go in
 *              (textConsent, patientPhone, siteCapability, wikiTags), exactly as today's drawer
 *              computes them.
 *   sends      today's store actions with the rules' payload builders:
 *                Phone / Text / Email  saveBlockReason → tasks/addUpdateFollowupTask (buildSavePayload)
 *                Payment link          paymentSendBlock (textBlock too) → moolah/createMoolahPaymentRecord
 *                                      or teamio/createPaymentLink → teamio/sendTextMsg, the body
 *                                      checked with textSendOutcome + optOutResultFromSendFailure
 *                note edit             tasks/editFollowupNote (buildEditNotePayload)
 *                early wizard          tasks/updateFollowupDate (buildWizardDatePayload)
 *                AI call (SIM)         triggerAutorecallCall (buildAiCallPayload)
 *   view       DrawerLayout + the presentational pieces in src/components/Followups/drawer/, with
 *              the store-bound children mounted HERE in their slots: the existing SmsGuidance (the
 *              one SMS cost counter — no cost prompt), FeatureRequestButton (literal keys) and
 *              StripePaymentModal (the in-office card on a Stripe Connect site, as today).
 *
 * Deliberate differences from CreateFollowup.vue (beyond the rule fixes 1, 3, 4, 6–10 the rules
 * already carry — drawer plan "6a/6b notes"):
 *   • bug 10, second half: tasks with no parent row → the save is REFUSED with
 *     SAVE_MESSAGES.noParentTask (today: a TypeError, nothing said).
 *   • a settings read that throws today (a missing per-attempt frequency) leaves the next date EMPTY
 *     so it must be picked (today: the date silently stays at "today").
 *   • Escape closes the drawer when no follow-up form is open and nothing is being typed or saved
 *     (today: Escape does nothing). Background clicks still do nothing.
 *   • opening Payment link starts from an empty message (today it inherits the Phone note).
 *   • template HTML is turned into text with DOMParser (today: innerHTML on a detached DIV, which
 *     still loads <img> sources) — same text out.
 *
 * ⛔ No data / computed / method named errors / fields (vee-validate), no prop named patNum /
 * siteID / loading (mixin shadowing) — the mixin's patNum / siteID / user / allSettings are used.
 */
import moment from 'moment-timezone';
import { mapGetters } from 'vuex';
import mixin from '@/mixin.js';
import PopupErrorDisplay from '@/components/ErrorDisplay/PopupErrorDisplay';
import SmsGuidance from '@/components/SmsGuidance.vue';
import FeatureRequestButton from '@/components/FeatureRequestButton/FeatureRequestButton.vue';
import FeatureRequestNote from '@/components/FeatureRequestButton/FeatureRequestNote.vue';
import StripePaymentModal from '@/components/StripePaymentModal/StripePaymentModal.vue';
import { triggerAutorecallCall } from '@/api/autorecall';
import { isSmsEnabled, isEmailEnabled, hasSmsNumber } from '@/utils/siteCapability';
import { withoutReviewRequestTemplates } from '@/utils/wikiTags';
import { isTextablePhone, describePhoneProblem } from '@/utils/patientPhone.js';
import { getTextBlockReason, optOutResultFromSendFailure } from '@/utils/textConsent.js';
import { setDrawerUiChoice, browserStorage } from '@/utils/followups/uiVersion.js';

import {
  drawerQueueOf, readDrawerData, responsibleEmployeeIdsOf, maxFollowupsOf, copyToCommLogOf, settingValue,
  TEXTED_STATUS_LABEL, EMAILED_STATUS_LABEL, PAYMENT_TEMPLATE_TAG,
} from '@/utils/followups/drawer/drawerQueues.js';
import { alertConditions, resolveDrawerAlert, attemptGateOpen } from '@/utils/followups/drawer/drawerAlerts.js';
import {
  resolveDrawerMethods, templatesFor, paymentSendBlock, paymentRoute, isStripeConnectSite, saveButtonState,
} from '@/utils/followups/drawer/drawerMethods.js';
import {
  nextFollowupDate, dateAfterStatusChange, isDateEditable, canOverride, outcomeNoteKind,
} from '@/utils/followups/drawer/drawerNextDate.js';
import {
  messageStatusID, selectedStatusName, saveBlockReason, buildSavePayload, buildCloseResult, closeWithoutSave,
  nextVisitOf, buildEditNotePayload, buildWizardDatePayload, buildMoolahRecordPayload, buildStripeLinkPayload,
  buildPaymentTextPayload, stripePaymentText, textSendOutcome, paymentSentMessage, buildAiCallPayload,
  composePhoneNote, emailNoteForTemplate, smsTemplateVariables, renderSmsTemplate,
} from '@/utils/followups/drawer/drawerPayload.js';
import { buildDrawerTabs, withTasksTab } from '@/utils/followups/drawer/drawerTabs.js';
import {
  buildDrawerFacts, contactsOf, addressOf, hasPremed, firstNameOf, practiceNumberLabel,
} from '@/utils/followups/drawer/drawerFacts.js';

import DrawerLayout from '@/components/Followups/drawer/DrawerLayout.vue';
import DrawerHeader from '@/components/Followups/drawer/DrawerHeader.vue';
import DrawerFacts from '@/components/Followups/drawer/DrawerFacts.vue';
import DrawerAttemptProgress from '@/components/Followups/drawer/DrawerAttemptProgress.vue';
import DrawerAlert from '@/components/Followups/drawer/DrawerAlert.vue';
import DrawerEarlyWizard from '@/components/Followups/drawer/DrawerEarlyWizard.vue';
import DrawerMethodPicker from '@/components/Followups/drawer/DrawerMethodPicker.vue';
import DrawerPhoneForm from '@/components/Followups/drawer/DrawerPhoneForm.vue';
import DrawerTextForm from '@/components/Followups/drawer/DrawerTextForm.vue';
import DrawerEmailForm from '@/components/Followups/drawer/DrawerEmailForm.vue';
import DrawerPaymentForm from '@/components/Followups/drawer/DrawerPaymentForm.vue';
import DrawerAiCallForm from '@/components/Followups/drawer/DrawerAiCallForm.vue';
import DrawerNextDate from '@/components/Followups/drawer/DrawerNextDate.vue';
import DrawerOutcomeNote from '@/components/Followups/drawer/DrawerOutcomeNote.vue';
import DrawerTabs from '@/components/Followups/drawer/DrawerTabs.vue';
import DrawerFooter from '@/components/Followups/drawer/DrawerFooter.vue';

/**
 * Today's email template tag (CreateFollowup.vue EmailWikiPagesForFollowup). It lives here, not in
 * the rule files: the file that lists email templates must also drop review-request pages
 * (withoutReviewRequestTemplates — template-variable-validation's PICKERS guard).
 */
const EMAIL_TEMPLATE_TAG = 'email-template';

const LONG_DATE = 'dddd, MMMM D, YYYY';

/** Template HTML → text, as today's DIV textContent does, without loading anything it references. */
function stripHtml(html) {
  try {
    const doc = new window.DOMParser().parseFromString(String(html), 'text/html');
    return (doc.body && doc.body.textContent) || '';
  } catch (e) {
    return String(html).replace(/<[^>]*>/g, '');
  }
}

export default {
  name: 'FollowupDrawer',
  components: {
    PopupErrorDisplay, SmsGuidance, FeatureRequestButton, FeatureRequestNote, StripePaymentModal,
    DrawerLayout, DrawerHeader, DrawerFacts, DrawerAttemptProgress, DrawerAlert, DrawerEarlyWizard,
    DrawerMethodPicker, DrawerPhoneForm, DrawerTextForm, DrawerEmailForm, DrawerPaymentForm,
    DrawerAiCallForm, DrawerNextDate, DrawerOutcomeNote, DrawerTabs, DrawerFooter,
  },
  mixins: [mixin],
  props: {
    teamioPatientID: { type: String, default: null },
    claimNum: { type: [Number, String], default: undefined },
    results: { type: Object, default: null },
    detailPageName: { type: String, default: null },
    query: { type: Object, default: null },
    // From followupActions.openDrawer only — see the header.
    onClassicDrawer: { type: Function, default: null },
    onRestart: { type: Function, default: null },
  },
  data() {
    return {
      loadingData: true,
      resultSets: null,
      today: moment().format('YYYY-MM-DD'),
      layout: 'desktop',
      method: null,
      statusID: 0,
      followupDate: null,
      followupDateVisible: true,
      dateFrequency: null,
      overridden: false,
      dateBeforeOverride: null,
      followupNote: '',
      selectedWikiPageID: null,
      templateLinkMap: {},
      smsPreviewNote: '',
      wikiPages: [],
      smsBlocked: false,
      attemptInProgress: false,
      paymentInProgress: false,
      aiCallInProgress: false,
      aiCallResult: null,
      wizardBusy: false,
      wizardStepOverride: 1,
      wizardMovedTo: null,
      activeTab: null,
      savingNoteId: null,
      taggedTasks: [],
      restartConfirm: false,
      restarting: false,
    };
  },
  computed: {
    ...mapGetters({ getTasksTaggedWithPatient: 'tasks/getTasksTaggedWithPatient' }),

    queue() {
      return drawerQueueOf(this.detailPageName);
    },
    drawerData() {
      return this.resultSets ? readDrawerData(this.detailPageName, this.resultSets) : null;
    },
    patient() {
      return this.drawerData ? this.drawerData.patient : null;
    },
    statuses() {
      return this.drawerData ? this.drawerData.statuses : null;
    },
    tasks() {
      return this.drawerData ? this.drawerData.tasks : null;
    },
    settingsLoaded() {
      return !!this.allSettings;
    },
    timezone() {
      return settingValue(this.allSettings, 'GENERAL_TIMEZONE') || '';
    },
    site() {
      return this.getSite ? this.getSite(this.siteID) : null;
    },

    // ---- the rules ---------------------------------------------------------------------------
    conditions() {
      const d = this.drawerData || {};
      return alertConditions({
        detailPageName: this.detailPageName,
        settingsLoaded: this.settingsLoaded,
        responsibleIds: responsibleEmployeeIdsOf(this.detailPageName, this.allSettings),
        maxFollowups: maxFollowupsOf(this.detailPageName, this.allSettings),
        statusUpdatedAtField: this.queue ? this.queue.statusUpdatedAtField : null,
        userID: this.userID,
        patNum: this.patNum,
        today: this.today,
        now: moment().format('YYYY-MM-DDTHH:mm:ss'),
        tasks: d.tasks,
        mainParentTask: d.mainParentTask,
        assignmentRow: d.assignmentRow,
        familyAppts: d.familyAppts,
      });
    },
    alert() {
      return resolveDrawerAlert(this.conditions, {
        detailPageName: this.detailPageName,
        wizardStep: this.wizardStepOverride,
        collectPaymentFormVisible: this.method === 'payment',
      });
    },
    gateOpen() {
      return attemptGateOpen(this.conditions);
    },
    // textConsent's answer — fed to BOTH Text and Payment link (bug 4).
    textBlock() {
      return getTextBlockReason({ patient: this.patient, timezone: this.timezone });
    },
    phoneTextable() {
      return isTextablePhone(this.patient && this.patient.WirelessPhone);
    },
    phoneProblem() {
      return describePhoneProblem(this.patient && this.patient.WirelessPhone);
    },
    smsEnabled() {
      return isSmsEnabled(this.allSettings);
    },
    methodList() {
      return resolveDrawerMethods({
        followupType: this.queue ? this.queue.followupType : '',
        patient: this.patient,
        settingsLoaded: this.settingsLoaded,
        allSettings: this.allSettings,
        smsEnabled: this.smsEnabled,
        smsOutboundOff: hasSmsNumber(this.allSettings) && !this.smsEnabled,
        emailEnabled: isEmailEnabled(this.allSettings),
        textBlock: this.textBlock,
        phoneTextable: this.phoneTextable,
        phoneProblem: this.phoneProblem,
        hasStatuses: !!(this.statuses && this.statuses.length > 0),
        gateOpen: this.gateOpen,
        site: this.site,
        isSuperAdmin: this.isSuperAdmin,
        aiCallInProgress: this.aiCallInProgress,
      });
    },
    methodByKey() {
      return this.methodList.reduce((o, m) => { o[m.key] = m; return o; }, {});
    },
    isStripeConnect() {
      return isStripeConnectSite(this.site);
    },
    smsStatusID() {
      return messageStatusID(this.queue, this.statuses, TEXTED_STATUS_LABEL);
    },
    emailStatusID() {
      return messageStatusID(this.queue, this.statuses, EMAILED_STATUS_LABEL);
    },

    // ---- templates ---------------------------------------------------------------------------
    smsTemplates() {
      return templatesFor('sms', { wikiPages: this.wikiPages, smsTemplateTag: this.queue ? this.queue.smsTemplateTag : undefined }) || [];
    },
    emailTemplates() {
      return templatesFor('email', {
        wikiPages: this.wikiPages,
        emailEligiblePages: withoutReviewRequestTemplates(this.wikiPages),
        emailTemplateTag: EMAIL_TEMPLATE_TAG,
      }) || [];
    },
    paymentTemplates() {
      return (this.wikiPages || []).filter(w => w.Tags && w.Tags.includes(PAYMENT_TEMPLATE_TAG));
    },
    smsTemplateOptions() {
      return this.smsTemplates.map(w => ({ value: w.WikiPageID, label: w.Name }));
    },
    emailTemplateOptions() {
      return this.emailTemplates.map(w => ({ value: w.WikiPageID, label: w.Name }));
    },
    paymentTemplateOptions() {
      return this.paymentTemplates.map(w => ({ value: w.WikiPageID, label: w.Name }));
    },

    // ---- the view ----------------------------------------------------------------------------
    headerTitle() {
      return this.queue ? `${this.queue.title} Follow-up` : 'Follow-up';
    },
    firstName() {
      return firstNameOf(this.patient);
    },
    demographics() {
      const p = this.patient || {};
      const parts = [];
      if (p.Birthdate) {
        const age = moment().diff(p.Birthdate, 'years', false);
        if (Number.isFinite(age)) parts.push(`${this.formatAge(age)} yrs`);
      }
      if (p.Gender !== undefined && p.Gender !== null) parts.push(p.Gender === 1 ? 'F' : 'M');
      return parts.join(' · ');
    },
    premed() {
      return hasPremed(this.patient);
    },
    contacts() {
      return contactsOf(this.patient);
    },
    address() {
      return addressOf(this.patient);
    },
    facts() {
      return buildDrawerFacts({
        detailPageName: this.detailPageName,
        data: this.drawerData,
        today: this.today,
        formatDate: ymd => this.formatYmd(ymd),
        formatMoney: n => this.moneyLabel(n),
        employeeName: id => this.getEmployeeName(id),
      });
    },
    tabs() {
      const tabs = buildDrawerTabs({
        detailPageName: this.detailPageName,
        data: this.drawerData,
        patNum: this.patNum,
        remindersPaidFor: settingValue(this.allSettings, 'APPT_REMINDER_PAID_FOR') === 'TRUE',
      });
      return withTasksTab(tabs, this.taggedTasks);
    },
    tabCtx() {
      return {
        formatDate: v => this.formatDate(v),
        formatDateTime: v => this.commDateTime(v),
        formatMoney: n => this.moneyLabel(n),
        formatTooth: n => (this.getCountry === 'CANADA' ? this.convertUniversalToFDI(n) : n),
        employeeName: id => this.getEmployeeName(id),
        ageOf: b => (b ? moment().diff(b, 'years', false) : ''),
        canEditNotes: !!(this.$can && this.$can('edit', 'followupNote')),
        savingNoteId: this.savingNoteId,
      };
    },
    nextAttemptLabel() {
      const t = this.tasks && this.tasks[0];
      return t && t.FollowupDate ? this.formatYmd(t.FollowupDate) : '';
    },
    scheduledLabel() {
      const t = this.tasks && this.tasks[0];
      return t && t.FollowupDate ? this.formatYmd(t.FollowupDate) : '';
    },
    assigneeName() {
      const a = this.drawerData && this.drawerData.assignmentRow;
      if (!this.alert || this.alert.key !== 'notResponsible' || !a || a.EmployeeManuallyAssigned !== 1) return '';
      if (a.AssignedToEmpID === null || a.AssignedToEmpID === undefined) return '';
      const name = this.getEmployeeName(a.AssignedToEmpID);
      return name && name !== 'N/A' ? name : '';
    },
    canRestart() {
      return typeof this.onRestart === 'function';
    },
    showClassic() {
      return this.isSuperAdmin === true && typeof this.onClassicDrawer === 'function';
    },
    lockText() {
      return `Teammates see ${this.firstName || 'this patient'} as in use while this panel is open.`;
    },
    practiceNumber() {
      return practiceNumberLabel(settingValue(this.allSettings, 'GENERAL_PHONE'));
    },
    copiesToCommlog() {
      return String(copyToCommLogOf(this.detailPageName, this.allSettings) || '').toUpperCase() === 'TRUE';
    },
    statusOptions() {
      const field = this.queue ? this.queue.statusLabelField : null;
      return (this.statuses || []).map(s => ({ value: s.FollowupStatusID, label: field ? s[field] : '' }));
    },
    selectedStatus() {
      return (this.statuses || []).find(s => s.FollowupStatusID === this.statusID) || null;
    },
    outcomeKind() {
      return outcomeNoteKind(this.selectedStatus);
    },
    nextDateProps() {
      return {
        visible: this.followupDateVisible,
        date: this.followupDate,
        editable: isDateEditable(this.detailPageName),
        canOverride: canOverride(this.detailPageName, this.overridden),
        overridden: this.overridden,
        today: this.today,
        officeLabel: this.followupDate ? moment(this.followupDate).format(LONG_DATE) : '',
        frequency: this.dateFrequency,
      };
    },
    saving() {
      return this.attemptInProgress || this.paymentInProgress || this.aiCallInProgress;
    },
    saveState() {
      return saveButtonState(this.method || 'phone', {
        statusID: this.statusID,
        message: this.followupNote,
        templateID: this.selectedWikiPageID,
        attemptInProgress: this.attemptInProgress || this.paymentInProgress,
        smsBlocked: this.smsBlocked,
        aiInProgress: this.aiCallInProgress,
      });
    },
    nextVisit() {
      return nextVisitOf({
        detailPageName: this.detailPageName,
        scheduledAppointments: this.drawerData ? this.drawerData.scheduledAppointments : null,
        now: moment().format('YYYY-MM-DDTHH:mm:ss'),
      });
    },
  },
  watch: {
    tasksLoaded() {
      this.taggedTasks = this.getTasksTaggedWithPatient(this.patNum) || [];
    },
  },
  created() {
    this.load();
  },
  mounted() {
    // Capture phase: read the drawer's state BEFORE a modal or a form handles the same key.
    document.addEventListener('keydown', this.onKeydown, true);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown, true);
  },
  methods: {
    // ---- loading (today's init()) --------------------------------------------------------------
    async load() {
      this.loadingData = true;
      try {
        this.setErrored2(false);
        this.setMessage2('');
        let r = this.results;
        if (!r && this.query) r = await this.$store.dispatch('teamio/runQuery', this.query);
        this.resultSets = r && Array.isArray(r.data) && r.data.length > 0 ? JSON.parse(JSON.stringify(r.data)) : null;

        if (!this.tasksLoaded) {
          if (this.user && (this.user.Role === 'Super Admin' || this.user.Role === 'Admin')) {
            await this.$store.dispatch('tasks/getTasksForSite', { siteID: this.siteID, employeeID: this.userID });
          } else {
            await this.$store.dispatch('tasks/getTasksForEmployee', { siteID: this.siteID, employeeID: this.userID });
          }
        }
        this.taggedTasks = this.getTasksTaggedWithPatient(this.patNum) || [];
      } catch (err) {
        console.error('[FOLLOWUP DRAWER] load failed:', err);
      } finally {
        this.today = moment().format('YYYY-MM-DD');
        this.loadingData = false;
      }
    },

    // ---- formatting ----------------------------------------------------------------------------
    formatYmd(v) {
      return v ? moment(v).format(this.dateFormatStr) : '';
    },
    commDateTime(v) {
      if (!v) return '';
      const fmt = `${this.dateFormatStr} hh:mm a`;
      const m = this.timezone ? moment(v).tz(this.timezone) : moment(v);
      return m.isValid() ? m.format(fmt) : '';
    },
    moneyLabel(n) {
      if (n === null || n === undefined || n === '') return '';
      const v = Number(n);
      if (!Number.isFinite(v)) return '';
      const symbol = settingValue(this.allSettings, 'GENERAL_CURRENCY_SYMBOL') || '$';
      const body = Math.abs(v).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
      return `${v < 0 ? '-' : ''}${symbol}${body}`;
    },
    nowDisplay() {
      return moment().format(`${this.dateFormatStr} hh:mm A`);
    },
    dateDisplay(ymd) {
      return ymd ? moment(ymd).format(this.dateFormatStr) : null;
    },

    // ---- methods (today's showAttempt / showPaymentAttempt) -----------------------------------
    async onSelectMethod(key) {
      const m = this.methodByKey[key];
      if (!m) return;
      if (key === 'payment') {
        await this.openPayment();
        return;
      }
      if (key === 'ai') {
        this.wizardStepOverride = 1;
        this.method = 'ai';
        this.aiCallResult = null;
        return;
      }
      // drawerMethods' click guard = today's showAttempt: no statuses → toast; Text that cannot be
      // sent (opted out / an unusable mobile) → textConsent's or patientPhone's message.
      if (m.clickError) {
        this.$toast.error(m.clickError);
        return;
      }
      if (!m.opensForm) return; // the attempt gate is closed — the alert says why (as today)
      this.wizardStepOverride = 1;
      this.smsBlocked = false;
      if (key === 'sms' || key === 'email') {
        const res = await this.$store.dispatch('wiki/getWikiPages', { siteID: this.siteID });
        this.wikiPages = (res && res[0]) || [];
        // A template picked on another form never rides along (today it could become the email's).
        this.selectedWikiPageID = null;
      }
      this.followupNote = '';
      if (key === 'sms') {
        this.templateLinkMap = {};
        this.smsPreviewNote = '';
      }
      this.method = key;
      this.applyOfficeDate();
    },

    async openPayment() {
      if (this.isStripeConnect) {
        if (this.$refs.stripePaymentModal) this.$refs.stripePaymentModal.open();
        return;
      }
      this.smsBlocked = false;
      const res = await this.$store.dispatch('wiki/getWikiPages', { siteID: this.siteID });
      this.wikiPages = (res && res[0]) || [];
      this.followupNote = '';
      this.selectedWikiPageID = null;
      this.templateLinkMap = {};
      this.method = 'payment';
    },

    /** calculateNextFollowupDate: the office date when a Phone / Text / Email form opens. */
    applyOfficeDate() {
      const n = nextFollowupDate({
        queue: this.queue,
        allSettings: this.allSettings,
        totalAttempts: this.conditions.totalAttempts,
        isLastAttempt: this.conditions.isLastAttempt,
        today: this.today,
      });
      if (!n.visible) {
        this.followupDateVisible = false;
        this.followupDate = null;
        return;
      }
      if (n.legacyError) return; // keep what the form holds (empty at first → must be picked)
      this.followupDate = n.date;
      this.dateFrequency = n.frequency;
    },

    // ---- Phone (today's updateFollowupNote) ----------------------------------------------------
    onStatusChange(id) {
      this.statusID = id;
      const after = dateAfterStatusChange({
        queue: this.queue,
        status: this.selectedStatus,
        statusID: id,
        overridden: this.overridden,
        currentDate: this.followupDate,
        allSettings: this.allSettings,
        totalAttempts: this.conditions.totalAttempts,
        isLastAttempt: this.conditions.isLastAttempt,
        today: this.today,
      });
      this.followupDateVisible = after.visible;
      this.followupDate = after.date;
      this.composeNote();
    },

    composeNote() {
      const statusName = selectedStatusName({
        queue: this.queue, statuses: this.statuses, statusID: this.statusID, medium: 'phone',
        smsStatusID: this.smsStatusID, emailStatusID: this.emailStatusID,
      });
      if (!statusName) return;
      const claim = this.drawerData && this.drawerData.claim;
      this.followupNote = composePhoneNote({
        existingNote: this.followupNote,
        followupType: this.queue.followupType,
        isClaims: this.detailPageName === 'AgingDetails',
        dosDisplay: claim && claim.DateService ? moment(claim.DateService).format('MM/DD/YYYY') : undefined,
        attemptNumber: this.conditions.totalAttempts + 1,
        nowDisplay: this.nowDisplay(),
        statusName,
        nextDateDisplay: this.dateDisplay(this.followupDate),
        isLastAttempt: this.conditions.isLastAttempt,
      });
    },

    onOverride() {
      this.overridden = true;
      this.dateBeforeOverride = this.followupDate;
    },

    onUseOffice() {
      this.overridden = false;
      this.followupDate = this.dateBeforeOverride;
      if (this.method === 'phone' && this.statusID) this.onStatusChange(this.statusID);
    },

    onDateChange(ymd) {
      this.followupDate = ymd;
    },

    // ---- Text / Payment link templates (today's updateSMSFollowupNote) ------------------------
    onSmsTemplateChange(id) {
      this.selectedWikiPageID = id;
      const wiki = (this.wikiPages || []).find(w => w.WikiPageID === id);
      if (!wiki) return;
      const rendered = renderSmsTemplate({
        content: wiki.Content,
        stripHtml,
        variables: smsTemplateVariables({
          patient: this.patient,
          siteID: this.siteID,
          user: this.user,
          site: this.site,
          patientBalance: this.drawerData ? this.drawerData.patientBalance : undefined,
          isOverdueRecall: this.detailPageName === 'OverdueRecallDetails',
          recallDueDisplay: this.patient && this.patient.RecallDue ? this.formatDate(this.patient.RecallDue) : undefined,
        }),
      });
      this.followupNote = rendered.text;
      this.templateLinkMap = rendered.linkMap;
      this.smsPreviewNote = rendered.preview;
    },

    onSmsGuidance(g) {
      this.smsBlocked = !!(g && g.blocked);
    },

    // ---- Email (today's updateEmailFollowupNote, bug 8 fixed) ---------------------------------
    onEmailTemplateChange(id) {
      this.selectedWikiPageID = id;
      const picked = emailNoteForTemplate({
        wikiPages: this.wikiPages,
        selectedWikiPageID: id,
        existingNote: this.followupNote,
        followupType: this.queue.followupType,
        attemptNumber: this.conditions.totalAttempts + 1,
        nowDisplay: this.nowDisplay(),
        statusName: selectedStatusName({
          queue: this.queue, statuses: this.statuses, statusID: this.statusID, medium: 'email',
          smsStatusID: this.smsStatusID, emailStatusID: this.emailStatusID,
        }),
        nextDateDisplay: this.dateDisplay(this.followupDate),
        isLastAttempt: this.conditions.isLastAttempt,
      });
      this.followupNote = picked.note;
    },

    // ---- Save --------------------------------------------------------------------------------
    onSave() {
      if (this.method === 'payment') return this.collectPayment();
      if (this.method === 'ai') return this.placeAiCall();
      if (this.method) return this.saveAttempt(this.method);
      return undefined;
    },

    /** The rules' save input — the same shape the agreement spec builds from the legacy drawer. */
    saveInput(medium) {
      const d = this.drawerData || {};
      return {
        queue: this.queue,
        medium,
        tasks: d.tasks,
        patientList: d.patientList,
        statuses: d.statuses,
        statusID: this.statusID,
        smsStatusID: this.smsStatusID,
        emailStatusID: this.emailStatusID,
        followupDate: this.followupDate,
        dateBeforeOverride: this.dateBeforeOverride,
        overridden: this.overridden,
        followupNote: this.followupNote,
        selectedWikiPageID: this.selectedWikiPageID,
        templateLinkMap: this.templateLinkMap,
        copyToCommLog: copyToCommLogOf(this.detailPageName, this.allSettings),
        allSettings: this.allSettings,
        siteID: this.siteID,
        patNum: this.patNum,
        teamioPatientID: this.teamioPatientID,
        userID: this.userID,
        claimNum: this.claimNum,
        user: this.user,
        now: moment().format('YYYY-MM-DDTHH:mm:ss'),
        today: moment().format('YYYY-MM-DD'),
        nextVisit: this.nextVisit,
      };
    },

    async saveAttempt(medium) {
      if (this.attemptInProgress) return false;
      const input = this.saveInput(medium);
      const block = saveBlockReason(Object.assign({}, input, {
        followupDateVisible: this.followupDateVisible,
        smsTemplatesAvailable: this.smsTemplates.length > 0,
        emailTemplatesAvailable: this.emailTemplates.length > 0,
        phoneTextable: this.phoneTextable,
        phoneProblem: this.phoneProblem,
      }));
      if (block) {
        this.$toast.error(block);
        return false;
      }
      const { payload } = buildSavePayload(input);
      try {
        this.attemptInProgress = true;
        await this.$store.dispatch('tasks/addUpdateFollowupTask', payload);
        this.$emit('closePanel', buildCloseResult(input));
        return true;
      } catch (err) {
        console.log(err);
        this.$toast.error('Failed to save follow-up attempt. Please try again.');
        return false;
      } finally {
        this.attemptInProgress = false;
      }
    },

    // ---- Payment link (today's collectPayment, bug 4 fixed) ------------------------------------
    async collectPayment() {
      if (this.paymentInProgress) return false;
      const block = paymentSendBlock({
        smsStatusID: this.smsStatusID,
        phoneTextable: this.phoneTextable,
        phoneProblem: this.phoneProblem,
        note: this.followupNote,
        site: this.site,
        textBlock: this.textBlock,
      });
      if (block) {
        this.$toast.error(block);
        return false;
      }
      const route = paymentRoute(this.site);
      const base = {
        siteID: this.siteID,
        patNum: this.patNum,
        patientBalance: this.drawerData.patientBalance,
        patient: this.patient,
      };
      try {
        this.paymentInProgress = true;
        this.setLoading2(true);
        if (route === 'moolah') {
          await this.$store.dispatch('moolah/createMoolahPaymentRecord', buildMoolahRecordPayload(base));
          const resp = await this.$store.dispatch('teamio/sendTextMsg', buildPaymentTextPayload({
            siteID: this.siteID, patNum: this.patNum, smsText: this.followupNote, user: this.user,
          }));
          this.assertTextSent(resp);
          this.$toast.success(paymentSentMessage('moolah'));
        } else {
          const link = await this.$store.dispatch('teamio/createPaymentLink', buildStripeLinkPayload(base));
          if (!link || !link.paymentUrl) throw new Error('Failed to create payment link - no URL returned');
          const resp = await this.$store.dispatch('teamio/sendTextMsg', buildPaymentTextPayload({
            siteID: this.siteID, patNum: this.patNum, smsText: stripePaymentText(this.followupNote, link), user: this.user,
          }));
          this.assertTextSent(resp);
          this.$toast.success(paymentSentMessage('stripe', link));
        }
        this.method = null;
        return true;
      } catch (err) {
        console.error('Error in collectPayment:', err);
        const msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Failed to send payment link';
        this.$toast.error(msg);
        return false;
      } finally {
        this.setLoading2(false);
        this.paymentInProgress = false;
      }
    },

    /** A /send-text-msg 200 body of { success:false } is a REFUSAL, not a send (assertTextSent). */
    assertTextSent(resp) {
      const data = resp && resp.data;
      const optOut = data && data.success === false ? optOutResultFromSendFailure(data) : null;
      const outcome = textSendOutcome(data, optOut);
      if (!outcome.sent) throw new Error(outcome.reason);
    },

    onStripeInOfficeSuccess(paymentInfo) {
      const amount = paymentInfo && Number(paymentInfo.amount);
      this.$toast.success(`Payment of $${Number.isFinite(amount) ? amount.toFixed(2) : '0.00'} collected successfully!`);
      this.closeWithoutSaving();
    },

    // ---- AI call (Super Admin, SIM) ------------------------------------------------------------
    async placeAiCall() {
      const ai = this.methodByKey.ai;
      if (this.aiCallInProgress || !ai || !ai.enabled) return;
      this.aiCallInProgress = true;
      this.aiCallResult = null;
      try {
        const result = await triggerAutorecallCall(buildAiCallPayload({ queue: this.queue, siteID: this.siteID, patNum: this.patNum }));
        if (result && result.success) this.aiCallResult = result;
        else this.$toast.error((result && (result.reason || result.message)) || 'AI Call was blocked or failed');
      } catch (err) {
        console.error('AI Call error:', err);
        const msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'AI Call failed';
        this.$toast.error(msg);
      } finally {
        this.aiCallInProgress = false;
      }
    },

    // ---- the early follow-up wizard (today's updateFollowupDateTo, bug 7 fixed) ----------------
    async moveFollowupDate(newDate) {
      const d = this.drawerData || {};
      const payload = buildWizardDatePayload({
        queue: this.queue,
        siteID: this.siteID,
        userID: this.userID,
        teamioPatientID: this.teamioPatientID,
        newDate,
        tasks: d.tasks,
        claimNum: this.claimNum,
        today: moment().format('YYYY-MM-DD'),
        mainParentTask: d.mainParentTask,
        responsibleSetting: settingValue(this.allSettings, this.queue.settings.responsible),
      });
      try {
        this.wizardBusy = true;
        this.setLoading2(true);
        await this.$store.dispatch('tasks/updateFollowupDate', payload);
        if (d.tasks && d.tasks.length > 0) d.tasks[0].FollowupDate = payload.FollowupDate;
        return true;
      } catch (err) {
        console.error('Error updating followup date:', err);
        const msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Failed to update follow-up date';
        this.$toast.error(msg);
        return false;
      } finally {
        this.setLoading2(false);
        this.wizardBusy = false;
      }
    },

    async onWizardMoveToday() {
      const today = moment().format('YYYY-MM-DD');
      if (await this.moveFollowupDate(today)) {
        this.$toast.success('Date set to today!');
        this.wizardMovedTo = today;
        this.wizardStepOverride = 2;
      }
    },

    async onWizardPickDate(ymd) {
      if (await this.moveFollowupDate(ymd)) {
        this.$toast.success('Date updated successfully!');
        this.wizardMovedTo = ymd;
        this.wizardStepOverride = 2;
      }
    },

    // ---- note edit (today's _editFollowupNote) -------------------------------------------------
    async onEditNote({ activity, oldNote, newNote }) {
      if (!activity) return;
      const payload = buildEditNotePayload({
        queue: this.queue,
        siteID: this.siteID,
        teamioPatientID: this.teamioPatientID,
        userID: this.userID,
        claimNum: this.claimNum,
        oldFollowupNote: oldNote,
        activity: { FollowupNote: newNote, TaskID: activity.TaskID },
        user: this.user,
        patNum: this.patNum,
        copyToCommLog: copyToCommLogOf(this.detailPageName, this.allSettings, { editNote: true }),
      });
      try {
        this.savingNoteId = activity.TaskID;
        await this.$store.dispatch('tasks/editFollowupNote', payload);
        const rows = (this.drawerData && this.drawerData.activities) || [];
        const row = rows.find(a => a.TaskID === activity.TaskID);
        if (row) row.FollowupNote = newNote;
      } catch (err) {
        console.log(err); // the note stays as it was; the store shows the error (PopupErrorDisplay), as today
      } finally {
        this.savingNoteId = null;
      }
    },

    // ---- restart (followupActions.restartFollowups) ---------------------------------------------
    async confirmRestart() {
      if (!this.canRestart || this.restarting) return;
      this.restarting = true;
      try {
        const ok = await this.onRestart();
        if (ok) {
          this.restartConfirm = false;
          this.method = null;
          this.statusID = 0;
          this.followupNote = '';
          await this.load();
        }
      } finally {
        this.restarting = false;
      }
    },

    // ---- leaving -------------------------------------------------------------------------------
    closeWithoutSaving() {
      this.$emit('closePanel', closeWithoutSave({ nextVisit: this.nextVisit }));
    },

    /** Super Admin "Classic drawer": remember the choice (drawer only), close, followupActions reopens. */
    useClassicDrawer() {
      setDrawerUiChoice(browserStorage(), 'old');
      if (typeof this.onClassicDrawer === 'function') this.onClassicDrawer();
      this.closeWithoutSaving();
    },

    openProfile() {
      const routeData = this.$router.resolve({ name: 'patientprofile', params: { patientID: this.teamioPatientID } });
      window.open(routeData.href, '_blank');
    },

    openPatient(patNum) {
      const routeData = this.$router.resolve({ name: 'patientprofile', params: { patientID: `${this.siteID}-${patNum}` } });
      window.open(routeData.href, '_blank');
    },

    /**
     * Escape closes the drawer — the design's behaviour — ONLY when nothing would be lost: no
     * follow-up form open, nothing saving, no restart question, no note being edited, no modal
     * (the in-office card) on top, and the key not typed into a field. Otherwise it does nothing,
     * as today (the panel library's own Escape stays disabled: it would close with no result).
     */
    escapeWouldLoseWork(e) {
      if (this.method !== null || this.saving || this.wizardBusy || this.restartConfirm || this.restarting || this.savingNoteId !== null) return true;
      if (this.$el && this.$el.querySelector && this.$el.querySelector('[data-test="fu-drawer-edit-text"]')) return true;
      if (typeof document !== 'undefined' && document.querySelector('.modal.show')) return true;
      const t = e && e.target;
      return !!(t && t.tagName && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
    },

    onKeydown(e) {
      if (!e || (e.key !== 'Escape' && e.keyCode !== 27) || e.defaultPrevented) return;
      if (this.loadingData) return;
      if (this.escapeWouldLoseWork(e)) return;
      this.closeWithoutSaving();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../components/Followups/drawer/drawer';

.fu-drc {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: $fu-neutral-25;

  > * { flex: 1 1 auto; min-height: 0; }
}

.fu-drc__state {
  @include fu-font-base;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 24px;
  text-align: center;
  color: $fu-text-muted;
  font-size: 14px;
  p { margin: 0; }
}

.fu-drc__state-title {
  @include fu-font-display;
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: $fu-text;
}

.fu-drc__spinner {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid $fu-neutral-200;
  border-top-color: $fu-brand;
  @include fu-spin;
}

.fu-drc__btn { @include fu-btn(primary, sm); }
.fu-drc__btn--ghost { @include fu-btn(ghost, sm); }

.fu-drc__confirm {
  @include fu-dr-warn-strip;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.fu-drc__confirm-text { margin: 0; font-size: 13px; line-height: 1.45; }
.fu-drc__confirm-actions { display: flex; flex-wrap: wrap; gap: 8px; }

.fu-drc__ai { display: flex; flex-direction: column; gap: 12px; }

.fu-drc__ai-result {
  padding: 12px 14px;
  border: 1px solid $fu-border;
  border-radius: 11px;
  background: $fu-surface;
  font-size: 13px;
  line-height: 1.5;
}

.fu-drc__ai-meta { margin-left: 8px; color: $fu-text-muted; }
.fu-drc__ai-transcript { margin: 8px 0 0; padding-left: 18px; }
.fu-drc__ai-reason { margin: 8px 0 0; color: $fu-text-muted; }
</style>
